import {
  GoogleAuthProvider, OAuthProvider, signInWithPopup, signInWithRedirect, getRedirectResult, signOut, onAuthStateChanged,
  sendSignInLinkToEmail, isSignInWithEmailLink, signInWithEmailLink,
} from 'https://www.gstatic.com/firebasejs/10.14.1/firebase-auth.js';
import { auth, db } from './firebase.js';
import { terminate, clearIndexedDbPersistence, waitForPendingWrites } from './fb/firestore.js';
import { ORG_DOMAIN, setOrgId } from './config.js';
import { clearGoogleToken } from './google-api.js';
import { clearEmptyMemo } from './db.js';
import { clearDriveCache } from './drive-cache.js';
import { LOSS_COPY, unsentWrites, signOutPlan } from './signout-guard.js';
import { clearAllSaveIntents } from './views/dr-sync-model.js';
// Static, not import(): a sign-out with no connection cannot fetch a chunk, and
// with no connection is exactly when this has to appear.
import { askBeforeLosing } from './views/dialog.js';

// ASP's own address (and local development) keeps ASP's sign-in exactly: Google
// only, the chooser hinted to aspconsulting.co.uk, and the "that is a personal
// account" refusal. Any other address (multi-tenant step 6) offers Google
// without the hint and an emailed sign-in link; which org a person joins, and
// whether their domain is allowed, is decided by their invite and the server.
export const ASP_HOST = 'track.aspconsulting.co.uk';
export const onAspHost = () => [ASP_HOST, 'localhost', '127.0.0.1'].includes(location.hostname);

// TWO GOOGLES (Karan 23/10/2026: sign-in split by address).
//  - ASP's address: the project's own Google provider, whose Google client is
//    Internal to ASP's Workspace — Google itself refuses anyone outside
//    aspconsulting.co.uk. hd only narrows the chooser; the rules, the server and
//    the sign-in gate are the enforcement.
//  - Any other address (app.trackapp.uk): an External Google client in its own
//    Cloud project ("Track Sign-in"), added to Firebase as the OpenID Connect
//    provider oidc.google-open, so a Gmail account can sign in. The sign-in gate
//    refuses a company-domain email (ASP) through it.
export const OPEN_GOOGLE = 'oidc.google-open';
function googleProvider() {
  if (onAspHost()) {
    const p = new GoogleAuthProvider();
    p.setCustomParameters({ hd: ORG_DOMAIN, prompt: 'select_account' });
    return p;
  }
  const p = new OAuthProvider(OPEN_GOOGLE);
  ['openid', 'email', 'profile'].forEach((s) => p.addScope(s));
  p.setCustomParameters({ prompt: 'select_account' });
  return p;
}
const provider = googleProvider();

// EMAIL-LINK SIGN-IN (step 6; the Email/Password provider with "email link" must
// be switched on in the Firebase console). The address is kept in this browser
// so the link, opened here, completes without asking again.
const LINK_EMAIL = 'track.signinEmail';
export async function sendEmailLink(email) {
  await sendSignInLinkToEmail(auth, email, { url: `${location.origin}/`, handleCodeInApp: true });
  try { localStorage.setItem(LINK_EMAIL, email); } catch (_) { /* storage blocked */ }
}
/** If this page was opened from a sign-in link, finish signing in. */
export async function completeEmailLink() {
  if (!isSignInWithEmailLink(auth, location.href)) return false;
  let email = null;
  try { email = localStorage.getItem(LINK_EMAIL); } catch (_) { /* storage blocked */ }
  if (!email) email = window.prompt('Confirm the email address this link was sent to');
  if (!email) return false;
  await signInWithEmailLink(auth, email, location.href);
  try { localStorage.removeItem(LINK_EMAIL); } catch (_) { /* storage blocked */ }
  history.replaceState(null, '', '/');
  return true;
}

// Redirect flow (not popup): popup sign-in fails its postMessage handshake on
// Firebase Hosting preview-channel domains (and can be blocked), which stranded
// the user on the sign-in screen after a successful Google verification. The
// redirect flow works on any authorised domain. onAuthStateChanged fires with
// the user on return; redirectResult() surfaces any error.
export function signIn() { return signInWithRedirect(auth, provider); }

/** How long ago this person last signed in with Google (ms), from the ID token's
 *  auth_time. 0 when it can't be read — never force a sign-in on a guess (the
 *  server still checks), so a misread can't loop the person through Google. */
export async function signInAgeMs() {
  try {
    const u = auth.currentUser; if (!u) return 0;
    const r = await u.getIdTokenResult();
    const at = Date.parse(r.authTime);
    return Number.isFinite(at) ? Date.now() - at : 0;
  } catch (_) { return 0; }
}

/** The sign-in gate's own words when it refused this sign-in (invite-only,
 *  wrong Google for the company, …), else null. A blocking function's refusal
 *  arrives as auth/internal-error carrying its message in JSON. */
export function gateRefusal(e) {
  const msg = String((e && e.message) || '');
  if (!/PERMISSION_DENIED|permission-denied|BLOCKING_FUNCTION/i.test(msg)) return null;
  const m = msg.match(/"message"\s*:\s*"([^"]{3,240})"/);
  return m ? m[1].replace(/\\u2019/g, '’') : 'Track did not let this account in.';
}
/** Only these mean "the popup itself could not run": try a full-page sign-in instead. */
export const POPUP_UNAVAILABLE = ['auth/popup-blocked', 'auth/operation-not-supported-in-this-environment', 'auth/web-storage-unsupported'];
export function signInPopup() { return signInWithPopup(auth, provider); }
export function redirectResult() { return getRedirectResult(auth); }
/**
 * Sign out. EVERY way out of Track comes through here, and sign-out deletes the
 * cache that queued writes wait in, so when any are waiting it asks first (v4.38.11).
 * Resolves false if the person chose to stay, so callers must not carry on.
 *   confirmLoss  the phone's own alert; the desktop dialog otherwise.
 *   automatic    nobody at the keyboard: never asks, keeps the cache if work is queued.
 *   force        a closed account (signout-guard.js): never asks, always clears.
 */
export async function logOut({ reload = true, automatic = false, force = false, confirmLoss = askBeforeLosing } = {}) {
  const pending = force ? false : await unsentWrites(() => waitForPendingWrites(db));
  const plan = signOutPlan({ pending, automatic, force });
  if (plan.ask && !(await confirmLoss(LOSS_COPY))) return false;
  await clearGoogleToken();   // forgets the server-held Google grant too (bounded)
  clearEmptyMemo();   // the next person at this browser is not this one
  // Mail keeps this tab's last Inbox (summaries) for a fast return; it goes too.
  try { Object.keys(sessionStorage).filter((k) => k.startsWith('track-mail-cache:')).forEach((k) => sessionStorage.removeItem(k)); } catch (_) { /* storage blocked */ }
  await Promise.race([clearDriveCache(), new Promise((r) => setTimeout(r, 1500))]);   // Files' cached Drive listings
  await signOut(auth);
  // SHARED DEVICES (multi-tenant step 6): Firestore keeps every document this
  // person read in the browser's IndexedDB. The next person — maybe from another
  // organisation — must not inherit it. Shut the database down, delete its
  // cache and the per-org copies (org.js), then start the page clean.
  try { Object.keys(localStorage).filter((k) => k.startsWith('track.org.')).forEach((k) => localStorage.removeItem(k)); } catch (_) { /* storage blocked */ }
  if (plan.clearCache) {
    clearAllSaveIntents();
    try { await terminate(db); await clearIndexedDbPersistence(db); } catch (e) { console.warn('[logout] cache not cleared', e && e.message); }
  } else console.warn('[logout] work is still queued, so this device’s cache was kept');
  if (reload) location.replace('/');
  return true;
}
export function onAuth(cb) { return onAuthStateChanged(auth, cb); }

/**
 * Resolve the signed-in user into a session. Forces a token refresh so custom
 * claims set by the sync (org, roles) are picked up immediately rather than
 * waiting up to an hour for the cached token to expire.
 */
export async function resolveSession(user) {
  // Force a token refresh so freshly-synced claims (org, roles) apply at once —
  // but NEVER let a slow/hung refresh block boot. A stalled forced-refresh here
  // left the whole app on a blank screen (Karan 16/07, esp. on deep-link loads).
  // Fall back to the cached token after 4s; its claims are good enough to boot.
  let res;
  try {
    res = await Promise.race([
      user.getIdTokenResult(true),
      new Promise((_, reject) => setTimeout(() => reject(new Error('token-refresh-timeout')), 4000)),
    ]);
  } catch (e) {
    res = await user.getIdTokenResult(false);
  }
  const claims = res.claims || {};
  const roles = Array.isArray(claims.roles) ? claims.roles : [];
  // The org comes from the claim (a display account carries it as `display`).
  const claimOrg = typeof claims.org === 'string' ? claims.org : (typeof claims.display === 'string' ? claims.display : null);
  const orgId = claimOrg ? setOrgId(claimOrg) : null;
  // The "personal account" refusal is ASP's address only; elsewhere the server
  // decides (invites, the org's sign-in domain).
  const domainOk = !onAspHost() || (user.email || '').toLowerCase().endsWith('@' + ORG_DOMAIN);
  return {
    uid: user.uid,
    phone: user.phoneNumber || null,
    email: user.email,
    displayName: user.displayName || user.email || (claims.supportSession ? 'Track support' : null),
    org: claims.org || null,
    roles,
    domainOk,
    // A DISPLAY ACCOUNT carries `display` INSTEAD of `org`, never both — that is
    // what denies the meeting-room iPad every rule in firestore.rules, all of
    // which gate on inOrg(). See Claims.js.
    display: !!orgId && claims.display === orgId,
    // Provisioned = this account's claims have been set. EITHER claim counts
    // (Karan 18/09): reading only `org` meant a display account — whose whole
    // design is to have no org claim — could never be provisioned, so
    // meetingroom@ sat on "access activates within about 15 minutes" for a day
    // and would have sat there forever. The screen was telling the truth about
    // a wait that was never going to end.
    provisioned: !!orgId && orgId === claimOrg && roles.length > 0,
    // Closed = a former colleague, marked inactive and never deleted (sign-in
    // "No longer active" state). Only true when the offboarding explicitly sets
    // the claim, so an active user is never mistaken for a leaver.
    closed: claims.active === false,
    // The platform admin (Traverse admin console, 22/10/2026): a claim set by
    // a one-off script, on an account that belongs to NO company. app.js sends
    // it to /admin and never to the company app.
    platformAdmin: claims.platformAdmin === true,
    // A support session (admin console › Support access, 23/10/2026): the
    // synthetic account the console signed in, read-only until supportUntil.
    support: claims.supportSession ? { readOnly: claims.readOnly === true, endsAt: Number(claims.supportUntil) || 0 } : null,
    closedAt: claims.accessEndedAt || claims.closedAt || null,
  };
}
